import { useI18n } from 'vue-i18n'
import { useFormatCurrency } from '@shared/plugins'

export interface ValueFormatters {
  locale: string
  /* `precision` undefined keeps the currency's own precision */
  formatCurrency: (
    value: number,
    currency: string,
    precision?: number
  ) => string
  /* `formatted` is the localised number shown, `count` picks the plural form */
  formatItems: (formatted: string, count: number) => string
}

/* the signature of the untyped $formatCurrency plugin */
type CurrencyFormatter = (
  value: number,
  currency: string,
  options?: { precision?: number }
) => string

/**
 * Formats a block value by its analytics unit: a currency code, `percent` or `productUnitsSold`. Anything else is a
 * plain number. Example: formatValue(1842, 'EUR', formatters) → '€1,842.00'; precision 0 → '€1,842'.
 */
export function formatValue(
  value: number,
  unit: string | undefined,
  formatters: ValueFormatters,
  precision?: number
): string {
  if (unit === 'percent') return `${value}%`
  if (unit && /^[A-Z]{3}$/.test(unit)) {
    return formatters.formatCurrency(value, unit, precision)
  }
  const number = new Intl.NumberFormat(formatters.locale, {
    maximumFractionDigits: 2
  }).format(value)
  return unit === 'productUnitsSold'
    ? formatters.formatItems(number, value)
    : number
}

/* formatValue bound to the dashboard locale and currency formatter */
export function useFormatValue(): (
  value: number,
  unit?: string,
  precision?: number
) => string {
  const { t, locale } = useI18n()
  const formatCurrency: CurrencyFormatter = useFormatCurrency()

  return (value, unit, precision) =>
    formatValue(
      value,
      unit,
      {
        locale: locale.value,
        formatCurrency: (amount, currency, currencyPrecision) =>
          formatCurrency(amount, currency, { precision: currencyPrecision }),
        formatItems: (formatted, count) =>
          t('aichat.main.blocks.units.items', { count: formatted }, count)
      },
      precision
    )
}
